import { ExpandOutlined, InboxOutlined, PlusOutlined, ShrinkOutlined } from "@ant-design/icons";
import { Affix, Button, Card, Col, Form, message, Row, Spin } from "antd";
import { PACKING_REPORT_TEMPLATE_UPDATE, PACKING_REPORT_TEMPLATE_VIEW } from "apiServices/API_ENDPOINTS";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { convertUndefinedToNull } from "utils/functions";
import { getData, putData } from "../../../../apiServices/common";
import { alertPop } from "../../../../apiServices/common/helper";
import { isMobileView } from "../../../Common/AppHelper";
import Header from "../../../Common/Header";
import AddLabelForm from "./AddLabelForm";
import AddPackingReportTemplateForm from "./AddPackingReportTemplateForm";
import AddTableForm from "./AddTableForm";

export default function EditPackingReportTemplate(props) {
  const { width } = props;

  const [viewData, setViewData] = useState();
  const [labelForms, setLabelForms] = useState([]);
  const [tableForms, setTableForms] = useState([]);
  const [allForms, setAllForms] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeAccordionKeys, setActiveAccordionKeys] = useState([]);
  const [activeTableAccordionKeys, setActiveTableAccordionKeys] = useState([]);
  const labelFormsRef = useRef(new Map());
  const tableFormsRef = useRef(new Map());
  const tableComponentRefs = useRef(new Map());

  let { id } = useParams();
  const formRef = useRef(null);
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const discartForm = () => {
    navigate("/ex-factory/packing-report-template");
  };

  const parseRowNumber = (value) => {
    if (value === "" || value === null || value === undefined) {
      return null;
    }
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
  };

  const hasSubHeaderInColumns = (columns) =>
    Array.isArray(columns) &&
    columns.some(
      (column) => column?.is_split && Array.isArray(column.split_columns) && column.split_columns.length > 0,
    );

  const computeTablePlacementMeta = (tableValues, tableFormData) => {
    const initialData = tableFormData?.initialData || {};
    const rawFromRow = tableValues?.from_row ?? initialData.from_row;
    const fromRow = parseRowNumber(rawFromRow);

    if (fromRow == null) {
      return {
        fromRow: null,
        toRow: null,
        hasHeader: false,
        hasSubHeader: false,
      };
    }

    const hasHeaderFlag = tableValues?.has_table_header ?? initialData.has_table_header ?? 0;
    const hasHeader = hasHeaderFlag === 1 || hasHeaderFlag === true;

    const columnsSource =
      (Array.isArray(tableValues?.columns) && tableValues.columns.length > 0
        ? tableValues.columns
        : Array.isArray(initialData.columns)
        ? initialData.columns
        : []) || [];

    const hasSubHeader = hasSubHeaderInColumns(columnsSource);

    const toRow = fromRow + (hasHeader ? 1 : 0) + (hasSubHeader ? 1 : 0);

    return {
      fromRow,
      toRow,
      hasHeader,
      hasSubHeader,
    };
  };

  const toggleExpandAll = () => {
    const allExpanded =
      activeAccordionKeys.length === labelForms.length &&
      activeTableAccordionKeys.length === tableForms.length &&
      (labelForms.length > 0 || tableForms.length > 0);

    if (allExpanded) {
      setActiveAccordionKeys([]);
      setActiveTableAccordionKeys([]);
    } else {
      const allLabelKeys = labelForms.map((_, idx) => `label-${idx}`);
      const allTableKeys = tableForms.map((_, idx) => `table-${idx}`);
      setActiveAccordionKeys(allLabelKeys);
      setActiveTableAccordionKeys(allTableKeys);
    }
  };

  const getPackingReportTemplateInfo = useCallback(async () => {
    setIsLoading(true);
    try {
      let res = await getData(`${PACKING_REPORT_TEMPLATE_VIEW}/${id}`);
      if (res) {
        let masterData = res?.data?.data;
        setViewData(masterData);

        if (masterData?.details && Array.isArray(masterData.details) && masterData.details.length > 0) {
          const sortedDetails = [...masterData.details].sort((a, b) => (a.position || 0) - (b.position || 0));

          const tableForms = sortedDetails
            .filter((detail) => detail.type === "table" && detail.table_data)
            .map((detail, index) => {
              const tableData = detail.table_data;

              const columns = tableData.headers
                ? tableData.headers.map((header) => ({
                    column_name: header.name || "",
                    set_value: header.variable_slug || null,
                    merge_from_column: header.merge_from_column || null,
                    merge_to_column: header.merge_to_column || null,
                    calculation_rule: header.calculation_rule || null,
                    is_split: header.has_sub_headers === 1,
                    split_columns:
                      header.has_sub_headers === 1 && header.sub_headers
                        ? header.sub_headers.map((subHeader, idx) => ({
                            id: subHeader.id || idx + 1,
                            column_name: subHeader.name || "",
                            set_value: subHeader.variable_slug || null,
                            calculation_rule: subHeader.calculation_rule || null,
                          }))
                        : null,
                  }))
                : [];

              return {
                id: detail.id || `existing-table-${index}`,
                initialData: {
                  table_name: tableData.name || "",
                  from_row: tableData.from_row ? parseInt(tableData.from_row) : null,
                  has_table_header: tableData.has_table_header || 0,
                  table_header: tableData.table_header_text || "",
                  columns: columns,
                },
              };
            });

          const labelForms = sortedDetails
            .filter((detail) => detail.type === "label" && detail.label_data)
            .map((detail, index) => ({
              id: detail.id || `existing-label-${index}`,
              selectedVariableLabel: detail?.label_data?.variable_value || null,
              initialData: detail?.label_data || null,
              selectedVariableType: detail?.label_data?.variable_type || null,
            }));

          if (tableForms.length > 0) {
            setTableForms(tableForms);

            const allTableKeys = tableForms.map((_, idx) => `table-${idx}`);
            setActiveTableAccordionKeys(allTableKeys);
          }

          if (labelForms.length > 0) {
            setLabelForms(labelForms);

            const allLabelKeys = labelForms.map((_, idx) => `label-${idx}`);
            setActiveAccordionKeys(allLabelKeys);
          }

          const combined = sortedDetails.map((detail) => {
            if (detail.type === "table") {
              const tableIndex = tableForms.findIndex((t) => t.id === detail.id);
              return {
                type: "table",
                data: tableForms[tableIndex],
                position: detail.position,
                tableIndex: tableIndex,
              };
            } else {
              const labelIndex = labelForms.findIndex((l) => l.id === detail.id);
              return {
                type: "label",
                data: labelForms[labelIndex],
                position: detail.position,
                labelIndex: labelIndex,
              };
            }
          });
          setAllForms(combined);
        }

        setIsDataLoaded(true);
      }
    } catch (error) {
      alertPop("error", "Failed to load template data");
    } finally {
      setIsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    getPackingReportTemplateInfo();
  }, [getPackingReportTemplateInfo]);

  const addLabelForm = async () => {
    let nextFromRow = null;
    let nextToRow = null;
    let nextFromColumn = null;
    let nextToColumn = null;

    if (allForms.length > 0) {
      const lastForm = allForms[allForms.length - 1];

      if (lastForm.type === "label") {
        const labelData = lastForm.data;
        const labelFormInstance = labelFormsRef.current.get(labelData.id);

        if (!labelFormInstance) {
          alertPop("error", "Previous form not found. Please try again.");
          return;
        }

        try {
          await labelFormInstance.validateFields();
        } catch (error) {
          alertPop("error", "Please fill all required fields in the previous label before adding a new one.");
          return;
        }

        const labelValues = labelFormInstance.getFieldsValue();
        const labelToRow = parseRowNumber(labelValues?.to_row ?? labelData?.initialData?.to_row);
        const labelFromRow = parseRowNumber(labelValues?.from_row ?? labelData?.initialData?.from_row);
        const labelFromColumn = labelValues?.from_column ?? labelData?.initialData?.from_column;
        const labelToColumn = labelValues?.to_column ?? labelData?.initialData?.to_column;

        if (labelToRow != null) {
          nextFromRow = labelToRow + 1;
          nextToRow = labelToRow + 1;
        } else if (labelFromRow != null) {
          nextFromRow = labelFromRow + 1;
          nextToRow = labelFromRow + 1;
        }

        if (labelFromColumn != null) {
          nextFromColumn = Number(labelFromColumn);
        }
        if (labelToColumn != null) {
          nextToColumn = Number(labelToColumn);
        }
      } else if (lastForm.type === "table") {
        const tableData = lastForm.data;
        const tableComponentRef = tableComponentRefs.current.get(tableData.id);

        if (!tableComponentRef) {
          alertPop("error", "Previous table form not found. Please try again.");
          return;
        }

        try {
          await tableComponentRef.validateFields();
        } catch (error) {
          alertPop("error", "Please fill all required fields in the previous table before adding a new label.");
          return;
        }

        const tableValues = await tableComponentRef.submit();
        const { fromRow: tableFromRow, toRow: tableToRow } = computeTablePlacementMeta(tableValues, tableData);

        if (tableToRow != null) {
          nextFromRow = tableToRow + 2;
          nextToRow = tableToRow + 2;
        } else if (tableFromRow != null) {
          nextFromRow = tableFromRow + 2;
          nextToRow = tableFromRow + 2;
        }
      }
    }

    const newFormData = {
      id: `new-${Date.now()}`,
      selectedVariableLabel: null,
      selectedVariableType: null,
      initialData:
        nextFromRow != null
          ? {
              from_row: nextFromRow,
              to_row: nextToRow,
              from_column: nextFromColumn,
              to_column: nextToColumn,
            }
          : null,
    };

    setLabelForms((prevForms) => {
      const updatedForms = [...prevForms, newFormData];
      setActiveAccordionKeys([`label-${updatedForms.length - 1}`]);

      setAllForms((prev) => [
        ...prev,
        {
          type: "label",
          data: newFormData,
          position: prev.length + 1,
          labelIndex: updatedForms.length - 1,
        },
      ]);

      return updatedForms;
    });
  };

  const deleteLabelForm = (index) => {
    const formToDelete = labelForms[index];
    labelFormsRef.current.delete(formToDelete.id);
    setLabelForms((prevForms) => {
      const updatedForms = prevForms.filter((_, i) => i !== index);

      if (updatedForms.length === 1) {
        setActiveAccordionKeys([`label-0`]);
      } else if (updatedForms.length > 0) {
        setActiveAccordionKeys([`label-${updatedForms.length - 1}`]);
      }

      setAllForms((prev) => prev.filter((item) => item.data.id !== formToDelete.id));

      return updatedForms;
    });
  };

  const updateSelectedVariableLabel = (formId, value) => {
    setLabelForms((prevForms) => {
      const updatedForms = prevForms.map((formData) =>
        formData.id === formId ? { ...formData, selectedVariableLabel: value } : formData,
      );

      setAllForms((prevAllForms) =>
        prevAllForms.map((formItem) => {
          if (formItem.type !== "label") return formItem;
          if (formItem.data.id !== formId) return formItem;

          const latestFormData = updatedForms.find((form) => form.id === formId);

          return {
            ...formItem,
            data: latestFormData || formItem.data,
          };
        }),
      );

      return updatedForms;
    });
  };

  const updateSelectedVariableType = (formId, variableType) => {
    setLabelForms((prevForms) => {
      const updatedForms = prevForms.map((formData) =>
        formData.id === formId ? { ...formData, selectedVariableType: variableType } : formData,
      );

      setAllForms((prevAllForms) =>
        prevAllForms.map((formItem) => {
          if (formItem.type !== "label") return formItem;
          if (formItem.data.id !== formId) return formItem;

          const latestFormData = updatedForms.find((form) => form.id === formId);

          return {
            ...formItem,
            data: latestFormData || formItem.data,
          };
        }),
      );

      return updatedForms;
    });
  };

  const registerForm = (formId, formInstance) => {
    labelFormsRef.current.set(formId, formInstance);
  };

  const registerTableForm = (formId, formInstance) => {
    tableFormsRef.current.set(formId, formInstance);
  };

  const addTableForm = async () => {
    let nextFromRow = null;

    if (allForms.length > 0) {
      const lastForm = allForms[allForms.length - 1];

      if (lastForm.type === "label") {
        const labelData = lastForm.data;
        const labelFormInstance = labelFormsRef.current.get(labelData.id);

        if (!labelFormInstance) {
          alertPop("error", "Previous form not found. Please try again.");
          return;
        }

        try {
          await labelFormInstance.validateFields();
        } catch (error) {
          alertPop("error", "Please fill all required fields in the previous form before adding a new table.");
          return;
        }

        const labelValues = labelFormInstance.getFieldsValue();
        const labelToRow = parseRowNumber(labelValues?.to_row ?? labelData?.initialData?.to_row);
        const labelFromRow = parseRowNumber(labelValues?.from_row ?? labelData?.initialData?.from_row);

        if (labelToRow != null) {
          nextFromRow = labelToRow + 2;
        } else if (labelFromRow != null) {
          nextFromRow = labelFromRow + 2;
        }
      } else if (lastForm.type === "table") {
        const tableData = lastForm.data;
        const tableComponentRef = tableComponentRefs.current.get(tableData.id);

        if (!tableComponentRef) {
          alertPop("error", "Previous table form not found. Please try again.");
          return;
        }

        try {
          await tableComponentRef.validateFields();
        } catch (error) {
          alertPop("error", "Please fill all required fields in the previous table before adding a new one.");
          return;
        }

        const tableValues = await tableComponentRef.submit();
        const { fromRow: tableFromRow, toRow: tableToRow } = computeTablePlacementMeta(tableValues, tableData);

        if (tableToRow != null) {
          nextFromRow = tableToRow + 1;
        } else if (tableFromRow != null) {
          nextFromRow = tableFromRow + 1;
        }
      }
    }

    const newFormData = {
      id: `new-table-${Date.now()}`,
      initialData: nextFromRow != null ? { from_row: nextFromRow } : null,
    };

    setTableForms((prevForms) => {
      const updatedForms = [...prevForms, newFormData];
      setActiveTableAccordionKeys([`table-${updatedForms.length - 1}`]);

      setAllForms((prev) => [
        ...prev,
        {
          type: "table",
          data: newFormData,
          position: prev.length + 1,
          tableIndex: updatedForms.length - 1,
        },
      ]);

      return updatedForms;
    });
  };

  const deleteTableForm = (index) => {
    const formToDelete = tableForms[index];
    tableFormsRef.current.delete(formToDelete.id);
    tableComponentRefs.current.delete(formToDelete.id);
    setTableForms((prevForms) => {
      const updatedForms = prevForms.filter((_, i) => i !== index);

      if (updatedForms.length === 0) {
        setActiveTableAccordionKeys([]);
      } else if (updatedForms.length === 1) {
        setActiveTableAccordionKeys([`table-0`]);
      } else {
        setActiveTableAccordionKeys([`table-${Math.min(index, updatedForms.length - 1)}`]);
      }

      setAllForms((prev) => prev.filter((item) => item.data.id !== formToDelete.id));

      return updatedForms;
    });
  };

  const updatePackingReportTemplate = async () => {
    try {
      const mainFormValues = form.getFieldsValue();

      const details = await Promise.all(
        allForms.map(async (formItem, index) => {
          if (formItem.type === "table") {
            const tableFormData = formItem.data;
            const componentRef = tableComponentRefs.current.get(tableFormData.id);

            if (!componentRef) {
              throw new Error(`Form instance not found for table at position ${index + 1}`);
            }

            const values = await componentRef.validateFields();
            const constructTableData = {
              name: values?.table_name ?? null,
              from_row: values?.from_row ?? null,
              has_table_header: values?.has_table_header ? 1 : 0,
              table_header_text: values?.table_header ?? null,
              headers: values?.columns?.map((header) => {
                return {
                  name: header?.column_name ?? null,
                  variable_reference: null,
                  variable_value: null,
                  merge_from_column: header?.merge_from_column ?? null,
                  merge_to_column: header?.merge_to_column ?? null,
                  variable_slug: header?.set_value ?? null,
                  calculation_rule: header?.calculation_rule ?? null,
                  has_sub_headers: header?.is_split ? 1 : 0,
                  sub_headers:
                    header?.is_split === 0
                      ? []
                      : header?.split_columns?.map((subHeader) => ({
                          name: subHeader?.column_name ?? null,
                          variable_slug: subHeader?.set_value ?? null,
                          calculation_rule: subHeader?.calculation_rule ?? null,
                        })) || [],
                };
              }),
            };

            return {
              position: index + 1,
              type: "table",
              table_data: {
                ...convertUndefinedToNull(constructTableData),
              },
            };
          } else {
            const labelFormData = formItem.data;
            const formInstance = labelFormsRef.current.get(labelFormData.id);

            if (!formInstance) {
              throw new Error(`Form instance not found for label at position ${index + 1}`);
            }

            const values = await formInstance.validateFields();

            const activeLabelForm = labelForms.find((form) => form.id === labelFormData.id);
            const variableValue = activeLabelForm?.selectedVariableLabel ?? labelFormData.selectedVariableLabel ?? null;
            const variableType = activeLabelForm?.selectedVariableType ?? labelFormData.selectedVariableType ?? null;
            const { variable_reference, ...restValues } = values || {};
            const sanitizedValues = convertUndefinedToNull(restValues);

            return {
              position: index + 1,
              type: "label",
              label_data: {
                ...sanitizedValues,
                variable_reference: variableType === "dropdown" ? variable_reference ?? null : null,
                variable_value: variableValue,
                // variable_type: variableType,
              },
            };
          }
        }),
      );

      const payload = {
        ...mainFormValues,
        details,
      };

      const response = await putData(`${PACKING_REPORT_TEMPLATE_UPDATE}/${id}`, payload);

      if (response && response?.code === 200) {
        alertPop("success", response?.message || "Packing report template updated successfully");
        navigate(`/ex-factory/packing-report-template/edit/${id}`);
        await getPackingReportTemplateInfo();
      } else {
        message.error("Something went wrong");
      }
    } catch (error) {
      alertPop("error", "Please check all form fields and try again.");
    }
  };

  return (
    <>
      <div style={{ filter: isLoading ? "blur(2px)" : "none", transition: "filter 0.3s ease" }}>
        <Affix offsetTop={0}>
          <Card className="p-fixed" style={{ width: `${isMobileView() ? "100%" : `${width}px`}` }}>
            <Header
              title="Update Packing Report Template"
              subTitle=""
              addLink="/"
              addText="Add New"
              actions={[
                <Button size="small" key={1} danger onClick={() => discartForm()} disabled={isLoading}>
                  Discard
                </Button>,
                ...(allForms.length > 0
                  ? [
                      <Button
                        size="small"
                        key={5}
                        type="default"
                        onClick={toggleExpandAll}
                        icon={
                          activeAccordionKeys.length === labelForms.length &&
                          activeTableAccordionKeys.length === tableForms.length &&
                          (labelForms.length > 0 || tableForms.length > 0) ? (
                            <ShrinkOutlined />
                          ) : (
                            <ExpandOutlined />
                          )
                        }
                      >
                        {activeAccordionKeys.length === labelForms.length &&
                        activeTableAccordionKeys.length === tableForms.length &&
                        (labelForms.length > 0 || tableForms.length > 0)
                          ? "Collapse All"
                          : "Expand All"}
                      </Button>,
                    ]
                  : []),
                <Button size="small" key={2} type="primary" ghost onClick={addTableForm} disabled={isLoading}>
                  <PlusOutlined /> Add Table
                </Button>,
                <Button size="small" key={3} type="primary" ghost onClick={addLabelForm} disabled={isLoading}>
                  <PlusOutlined /> Add Label
                </Button>,
                <Button size="small" key={4} type="primary" onClick={updatePackingReportTemplate} disabled={isLoading}>
                  Update Template
                </Button>,
              ]}
            />
          </Card>
        </Affix>

        <Row gutter={6} className="mt-2">
          <Col span={24}>
            <AddPackingReportTemplateForm ref={formRef} viewData={viewData} form={form} edit={true} id={id} />
          </Col>
        </Row>

        <Row gutter={6} className="mt-2">
          <Col span={24}>
            {isDataLoaded && allForms.length === 0 && (
              <Card style={{ textAlign: "center", padding: "40px 20px", backgroundColor: "#fafafa" }}>
                <InboxOutlined style={{ fontSize: 48, color: "#bfbfbf", marginBottom: 16 }} />
                <p style={{ fontSize: 16, color: "#8c8c8c", marginBottom: 8 }}>No tables or labels added yet</p>
                <p style={{ fontSize: 14, color: "#bfbfbf" }}>
                  Click "Add Table" or "Add Label" button above to get started
                </p>
              </Card>
            )}
            {isDataLoaded &&
              allForms.map((formItem, idx) => {
                if (formItem.type === "table") {
                  const tableFormData = formItem.data;
                  const tableIndex = formItem.tableIndex;
                  return (
                    <AddTableForm
                      key={tableFormData.id}
                      ref={(ref) => {
                        if (ref) {
                          tableComponentRefs.current.set(tableFormData.id, ref);
                        }
                      }}
                      formId={tableFormData.id}
                      viewData={viewData}
                      edit={true}
                      id={id}
                      index={tableIndex}
                      initialData={tableFormData.initialData}
                      onDelete={deleteTableForm}
                      onRegisterForm={registerTableForm}
                      tableForms={tableForms}
                      labelForms={labelForms}
                      activeKeys={activeTableAccordionKeys}
                      onAccordionChange={setActiveTableAccordionKeys}
                    />
                  );
                } else {
                  const labelFormData = formItem.data;
                  const labelIndex = formItem.labelIndex;
                  return (
                    <AddLabelForm
                      key={labelFormData.id}
                      formId={labelFormData.id}
                      viewData={viewData}
                      edit={true}
                      id={id}
                      index={labelIndex}
                      initialData={labelFormData.initialData}
                      selectedVariableLabel={labelFormData.selectedVariableLabel}
                      onUpdateVariableLabel={(value) => updateSelectedVariableLabel(labelFormData.id, value)}
                      onVariableTypeChange={updateSelectedVariableType}
                      onDelete={deleteLabelForm}
                      onRegisterForm={registerForm}
                      labelForms={labelForms}
                      tableForms={tableForms}
                      activeKeys={activeAccordionKeys}
                      onAccordionChange={setActiveAccordionKeys}
                    />
                  );
                }
              })}
          </Col>
        </Row>
      </div>

      {isLoading && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(255, 255, 255, 0.8)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <Spin size="large" tip="Loading template data..." />
        </div>
      )}
    </>
  );
}
